import { mdiEmail } from '@mdi/js';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { ISkillInputOptionFragment } from 'graphql/types';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { IconButton } from 'common/components/IconButton/IconButton';
import { UserEmailDialog } from 'user/components/UserEmailDialog';
import { AcademyInviteEmailDialog } from 'user/components/AcademyInviteEmailDialog';
import { SkillCertificateOfferEventDialog } from 'skill/components/SkillCertificateOfferEventDialog';
import { ESkillCertificateOfferEventDialogMode } from 'skill/types';
import { TCertificateOption } from 'certificate/types';

// Presence enables the "enroll in offer" / "notify about offer" options, which
// open the shared SkillCertificateOfferEventDialog
export interface IOfferEventEmailOptions {
    skill?: ISkillInputOptionFragment | null;
    certificate?: TCertificateOption | null;
}

interface IProps {
    anchorButton?: React.ReactElement;
    disabled?: boolean;
    userIds?: string[];
    offerEventOptions?: IOfferEventEmailOptions;
    onSendEmailComplete?(): void;
}

export const UserEmailDropdownMenu = ({
    anchorButton,
    disabled,
    userIds,
    offerEventOptions,
    onSendEmailComplete,
}: IProps) => {
    const [translate] = useTranslation();
    const [userEmailDialogOpen, setUserEmailDialogOpen] = useState(false);
    const [academyInviteEmailDialogOpen, setAcademyInviteEmailDialogOpen] =
        useState(false);
    const [offerEventDialogMode, setOfferEventDialogMode] =
        useState<ESkillCertificateOfferEventDialogMode | null>(null);

    const showOfferEventOptions = !!offerEventOptions;

    const anchor = anchorButton || (
        <IconButton
            color="inherit"
            disabled={disabled}
            iconPath={mdiEmail}
            iconSize="2.4rem"
        />
    );

    return (
        <>
            <DropdownMenu anchor={anchor}>
                {showOfferEventOptions && (
                    <MenuItem
                        onClick={() =>
                            setOfferEventDialogMode(
                                ESkillCertificateOfferEventDialogMode.Enrollment
                            )
                        }
                    >
                        {translate('skillsMatrix.enrollInOffer')}
                    </MenuItem>
                )}

                {showOfferEventOptions && (
                    <MenuItem
                        onClick={() =>
                            setOfferEventDialogMode(
                                ESkillCertificateOfferEventDialogMode.Notify
                            )
                        }
                    >
                        {translate('skillsMatrix.notifyAboutOffer')}
                    </MenuItem>
                )}

                <MenuItem onClick={() => setAcademyInviteEmailDialogOpen(true)}>
                    {translate('academyInviteEmail.buttonText')}
                </MenuItem>

                <MenuItem onClick={() => setUserEmailDialogOpen(true)}>
                    {translate('userEmail.buttonText')}
                </MenuItem>
            </DropdownMenu>

            <AcademyInviteEmailDialog
                open={academyInviteEmailDialogOpen}
                userIds={userIds}
                onClose={() => setAcademyInviteEmailDialogOpen(false)}
                onComplete={onSendEmailComplete}
            />

            <UserEmailDialog
                open={userEmailDialogOpen}
                userIds={userIds}
                onClose={() => setUserEmailDialogOpen(false)}
                onComplete={onSendEmailComplete}
            />

            {showOfferEventOptions && (
                <SkillCertificateOfferEventDialog
                    certificate={offerEventOptions?.certificate}
                    mode={offerEventDialogMode ?? undefined}
                    open={!!offerEventDialogMode}
                    skill={offerEventOptions?.skill}
                    userIds={userIds ?? []}
                    onClose={() => setOfferEventDialogMode(null)}
                    onComplete={onSendEmailComplete}
                />
            )}
        </>
    );
};
